import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { toggleTheme } from '../../store/themeSlice';
import { FiSun, FiMoon, FiChevronDown, FiUser, FiLogOut, FiBell, FiMoreVertical } from 'react-icons/fi';
import { useAuth } from '../../auth/AuthContext';
import { toast } from 'react-toastify';
import type { RootState } from '../../store/store';
import Modal from './Modal';
import Button from './Button';
import Input from './Input';
import tenantService from '../../services/tenant.service';
import type { Tenant } from '../../types/Tenant';
import changelogService from '../../services/changelog.service';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { deDE } from '@mui/x-date-pickers/locales';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/de';
import { IconButton, Menu, MenuItem } from '@mui/material';
import type { ChangelogEntry } from '../../services/changelog.service';
import { renderMarkdown } from '../../utils/renderMarkdown';

interface ChangeLogEntryFormValues {
  tenantIds: string[];
  date: Dayjs | null;
  updateText: string;
}

const formatChangelogDate = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('de-DE').format(date);
};

const sortChangelogEntriesByDateDesc = (entries: ChangelogEntry[]): ChangelogEntry[] =>
  [...entries].sort((left, right) => {
    const rightDate = new Date(right.date).getTime();
    const leftDate = new Date(left.date).getTime();
    return rightDate - leftDate;
  });

const Header: React.FC = () => {
  const { user, logout, isImpersonating, stopImpersonation } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const theme = useSelector((state: RootState) => state.themeSlice.theme);
  const darkMode = theme === 'dark';
  const isSuperAdmin = user?.role === 'SuperAdmin';
  const changeLogEntryForm = useForm<ChangeLogEntryFormValues>({
    defaultValues: {
      tenantIds: [],
      date: null,
      updateText: '',
    },
  });
  
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isChangeLogOpen, setIsChangeLogOpen] = useState(false);
  const [isChangeLogEntryOpen, setIsChangeLogEntryOpen] = useState(false);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [changeLogEntries, setChangeLogEntries] = useState<ChangelogEntry[]>([]);
  const [changeLogEntriesLoading, setChangeLogEntriesLoading] = useState(false);
  const [hasNewChangeLogEntries, setHasNewChangeLogEntries] = useState(false);
  const [changelogEntrySubmitting, setChangelogEntrySubmitting] = useState(false);
  const [changeLogActionMenu, setChangeLogActionMenu] = useState<{
    anchorEl: HTMLElement | null;
    entryId: string;
  }>({
    anchorEl: null,
    entryId: '',
  });
  const [changeLogDeleteDialogOpen, setChangeLogDeleteDialogOpen] = useState(false);
  const [changeLogDeleteSubmitting, setChangeLogDeleteSubmitting] = useState(false);
  const [editingChangeLogEntryId, setEditingChangeLogEntryId] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleToggleTheme = () => {
    dispatch(toggleTheme());
  };

  const handleChangeLogOpen = () => {
    setIsChangeLogOpen(true);
  };

  const handleChangeLogClose = () => {
    setIsChangeLogOpen(false);
  };

  const handleChangeLogEntryOpen = () => {
    setEditingChangeLogEntryId(null);
    changeLogEntryForm.reset({
      tenantIds: [],
      date: dayjs(),
      updateText: '',
    });
    setIsChangeLogEntryOpen(true);
  };

  const handleChangeLogEntryClose = () => {
    if (changelogEntrySubmitting) {
      return;
    }

    setIsChangeLogEntryOpen(false);
    setEditingChangeLogEntryId(null);
    changeLogEntryForm.clearErrors(['tenantIds', 'date', 'updateText']);
  };

  const handleCreateChangelogEntry = async () => {
    const values = changeLogEntryForm.getValues();
    changeLogEntryForm.clearErrors(['tenantIds', 'date', 'updateText']);
    let hasValidationError = false;
    const releaseDate = values.date;

    if (values.tenantIds.length === 0) {
      changeLogEntryForm.setError('tenantIds', {
        type: 'required',
        message: 'Fülle dieses Feld aus',
      });
      hasValidationError = true;
    }

    if (!releaseDate) {
      changeLogEntryForm.setError('date', {
        type: 'required',
        message: 'Fülle dieses Feld aus',
      });
      hasValidationError = true;
    }

    const updateText = values.updateText.trim();
    if (!updateText) {
      changeLogEntryForm.setError('updateText', {
        type: 'required',
        message: 'Fülle dieses Feld aus',
      });
      hasValidationError = true;
    }

    if (hasValidationError || !releaseDate) {
      return;
    }

    try {
      setChangelogEntrySubmitting(true);
      const payload = {
        tenantIds: values.tenantIds,
        date: releaseDate.toISOString(),
        updateText,
      };

      if (editingChangeLogEntryId) {
        const updatedEntry = await changelogService.updateEntry(editingChangeLogEntryId, payload);
        toast.success('Eintrag wurde aktualisiert.');
        setChangeLogEntries((currentEntries) => sortChangelogEntriesByDateDesc(
          currentEntries.map((entry) => entry.id === updatedEntry.id ? updatedEntry : entry)
        ));
      } else {
        const createdEntry = await changelogService.createEntry(payload);
        toast.success('Eintrag wurde erstellt.');
        setChangeLogEntries((currentEntries) => sortChangelogEntriesByDateDesc([
          createdEntry,
          ...currentEntries,
        ]));
      }

      changeLogEntryForm.reset({
        tenantIds: [],
        date: null,
        updateText: '',
      });
      setEditingChangeLogEntryId(null);
      setIsChangeLogEntryOpen(false);
    } catch {
      toast.error(editingChangeLogEntryId
        ? 'Eintrag konnte nicht aktualisiert werden.'
        : 'Eintrag konnte nicht erstellt werden.');
    } finally {
      setChangelogEntrySubmitting(false);
    }
  };

  const handleChangeLogEditClick = () => {
    const entry = changeLogEntries.find((item) => item.id === changeLogActionMenu.entryId);
    if (!entry) {
      handleChangeLogActionMenuClose();
      return;
    }

    changeLogEntryForm.reset({
      tenantIds: entry.tenantIds,
      date: dayjs(entry.date),
      updateText: entry.updateText,
    });
    setEditingChangeLogEntryId(entry.id);
    setIsChangeLogEntryOpen(true);
    setChangeLogActionMenu({
      anchorEl: null,
      entryId: '',
    });
  };

  const handleChangeLogActionMenuOpen = (event: React.MouseEvent<HTMLElement>, entryId: string) => {
    setChangeLogActionMenu({
      anchorEl: event.currentTarget,
      entryId,
    });
  };

  const handleChangeLogActionMenuClose = () => {
    setChangeLogActionMenu({
      anchorEl: null,
      entryId: '',
    });
  };

  const handleChangeLogDeleteClick = () => {
    setChangeLogDeleteDialogOpen(true);
    setChangeLogActionMenu((current) => ({
      ...current,
      anchorEl: null,
    }));
  };

  const handleChangeLogDeleteDialogClose = () => {
    if (changeLogDeleteSubmitting) {
      return;
    }

    setChangeLogDeleteDialogOpen(false);
  };

  const handleConfirmChangeLogDelete = async () => {
    const entryId = changeLogActionMenu.entryId;
    if (!entryId) {
      setChangeLogDeleteDialogOpen(false);
      return;
    }

    try {
      setChangeLogDeleteSubmitting(true);
      await changelogService.deleteEntry(entryId);
      setChangeLogEntries((currentEntries) => currentEntries.filter((entry) => entry.id !== entryId));
      toast.success('Eintrag wurde gelöscht.');
      setChangeLogDeleteDialogOpen(false);
      setChangeLogActionMenu({
        anchorEl: null,
        entryId: '',
      });
    } catch {
      toast.error('Eintrag konnte nicht gelöscht werden.');
    } finally {
      setChangeLogDeleteSubmitting(false);
    }
  };

  const loadChangeLogEntries = async (options: {
    markSeen?: boolean;
    showErrorToast?: boolean;
  } = {}) => {
    try {
      setChangeLogEntriesLoading(true);
      const result = await changelogService.listEntries();
      setChangeLogEntries(sortChangelogEntriesByDateDesc(result.entries));
      setHasNewChangeLogEntries(result.hasNew);
      if (options.markSeen) {
        await changelogService.markSeen();
        setHasNewChangeLogEntries(false);
      }
    } catch {
      if (options.showErrorToast !== false) {
        toast.error('Change log konnte nicht geladen werden.');
      }
    } finally {
      setChangeLogEntriesLoading(false);
    }
  };

  const loadChangeLogStatus = async () => {
    try {
      const status = await changelogService.getStatus();
      setHasNewChangeLogEntries(status.hasNew);
    } catch {
      // Keep the header quiet if the lightweight badge check fails.
    }
  };

  const handleDropdownToggle = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleProfileClick = () => {
    navigate('/profile');
    setIsDropdownOpen(false);
  };

  const handleLogoutClick = () => {
    logout();
    setIsDropdownOpen(false);
  };

  const handleStopImpersonation = async () => {
    try {
      await stopImpersonation();
      toast.success(t('impersonationStopped'));
      navigate('/tenants', { replace: true });
    } catch {
      toast.error(t('impersonationStopFailed'));
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    void loadChangeLogStatus();
  }, []);

  useEffect(() => {
    if (!isChangeLogOpen) {
      return;
    }

    setChangeLogEntries([]);
    void loadChangeLogEntries({ markSeen: true });
  }, [isChangeLogOpen]);

  const selectedTenantIds = changeLogEntryForm.watch('tenantIds');
  const watchedUpdateText = changeLogEntryForm.watch('updateText');

  useEffect(() => {
    if (watchedUpdateText.trim()) {
      changeLogEntryForm.clearErrors('updateText');
    }
  }, [watchedUpdateText, changeLogEntryForm]);

  useEffect(() => {
    if (!isChangeLogEntryOpen || !isSuperAdmin || tenants.length > 0 || tenantsLoading) {
      return;
    }

    setTenantsLoading(true);
    tenantService.getTenants({ view: 'list' })
      .then((tenantList) => {
        setTenants(tenantList);
      })
      .catch(() => {
        toast.error('Mandanten konnten nicht geladen werden');
      })
      .finally(() => {
        setTenantsLoading(false);
      });
  }, [isChangeLogEntryOpen, isSuperAdmin, tenants.length, tenantsLoading]);

  const tenantOptions = tenants.map((tenant) => ({
    name: tenant.name,
    value: tenant._id,
  }));
  const changeLogGridColumns = isSuperAdmin
?   'grid-cols-[1fr_32px] md:grid-cols-[82px_24px_1fr_32px]'
    : 'grid-cols-1 md:grid-cols-[82px_24px_1fr]';

  const getRoleLabel = () => {
    if (user?.role === 'SuperAdmin') return 'Super Admin';
    if (user?.role === 'User') return t('employee');
    return user?.role || '';
  };

  return (
    <div className="w-full">
      {isImpersonating && (
        <div className="w-full bg-red-600 text-white px-6 py-3 flex items-center justify-between gap-4">
          <div className="font-semibold text-sm">
            {t('impersonationActive')}: {user?.name} {user?.tenant?.name ? `(${user.tenant.name})` : ''}
          </div>
          <button
            onClick={handleStopImpersonation}
            className="bg-white text-red-700 px-4 py-2 rounded-md font-semibold shadow-md hover:shadow-lg transition-shadow"
          >
            {t('stopImpersonation')}
          </button>
        </div>
      )}
      <header 
        className="w-full px-6 py-4 border-b transition-colors duration-200 flex items-center justify-end"
        style={{
          backgroundColor: darkMode ? '#1F2937' : '#ffffff',
          borderColor: darkMode ? '#374151' : '#E5E7EB',
          color: darkMode ? '#F3F4F6' : '#1F2937'
        }}
      >
        {/* Right side - User info and dark mode toggle */}
        <div className="flex items-center space-x-4">
          {/* Change Log */}
          <button
            type="button"
            onClick={handleChangeLogOpen}
            className={`relative p-2 rounded-lg shadow-sm transition-all duration-200 border ${
              darkMode
                ? 'bg-gray-700 text-gray-200 border-gray-600 hover:bg-gray-600'
                : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200'
            }`}
            title={t('changeLog')}
            aria-label={t('changeLog')}
          >
            <FiBell className="w-4 h-4" />
            {hasNewChangeLogEntries ? (
              <span className="absolute right-1 top-1 h-2.5 w-2.5 rounded-full bg-yellow-400 ring-2 ring-white dark:ring-gray-700" />
            ) : null}
          </button>

          {/* Dark Mode Toggle */}
          <button
            onClick={handleToggleTheme}
            className={`p-2 rounded-lg shadow-sm transition-all duration-200 border ${
              darkMode
                ? 'bg-gray-700 text-gray-200 border-gray-600 hover:bg-gray-600'
                : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200'
            }`}
            title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            {darkMode ? (
              <FiSun className="w-4 h-4" />
            ) : (
              <FiMoon className="w-4 h-4" />
            )}
          </button>

          {/* User Profile Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={handleDropdownToggle}
              className="flex items-center space-x-3 hover:opacity-80 transition-opacity duration-200"
            >
            {/* User Info */}
            <div className="text-right">
              <div className="text-sm font-medium">
                {user?.name || ''}
              </div>
              <div 
                className="text-xs"
                style={{ color: darkMode ? '#9CA3AF' : '#6B7280' }}
              >
                {getRoleLabel()}
              </div>
            </div>

            {/* Avatar */}
            <div 
                className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium text-sm"
                style={{ backgroundColor: darkMode ? '#4B5563' : '#6B7280' }}
            >
              {user?.name ? user.name.charAt(0).toUpperCase() : ''}
            </div>

            {/* Dropdown Chevron */}
            <FiChevronDown 
                className={`w-4 h-4 transition-transform duration-200 ${
                  isDropdownOpen ? 'rotate-180' : ''
                }`}
              style={{ color: darkMode ? '#9CA3AF' : '#6B7280' }}
            />
            </button>

            {/* Dropdown Menu */}
            {isDropdownOpen && (
              <div 
                className={`absolute right-0 mt-2 w-48 rounded-lg shadow-lg border z-50 ${
                  darkMode
                    ? 'bg-gray-800 border-gray-600'
                    : 'bg-white border-gray-200'
                }`}
              >
                <div className="py-1">
                  {/* Profile Option */}
                  <button
                    onClick={handleProfileClick}
                    className={`w-full px-4 py-2 text-left flex items-center space-x-3 transition-colors duration-200 ${
                      darkMode
                        ? 'hover:bg-gray-700 text-gray-200'
                        : 'hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <FiUser className="w-4 h-4" />
                    <span className="text-sm">{t('profile')}</span>
                  </button>

                  {/* Logout Option */}
                  <button
                    onClick={handleLogoutClick}
                    className={`w-full px-4 py-2 text-left flex items-center space-x-3 transition-colors duration-200 ${
                      darkMode
                        ? 'hover:bg-gray-700 text-gray-200'
                        : 'hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <FiLogOut className="w-4 h-4" />
                    <span className="text-sm">{t('logout')}</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>
      <Modal
        open={isChangeLogOpen}
        close={handleChangeLogClose}
        title="Was ist neu?"
        size="md"
      >
        <div
          className="h-[calc(100dvh-145px)] min-h-[420px] overflow-y-auto"
        >
          {isSuperAdmin ? (
            <div className="mx-8 mt-4">
              <Button
                variant="secondary"
                text="+ Neuer Eintrag"
                customCSS="w-full"
                onClick={handleChangeLogEntryOpen}
              />
            </div>
          ) : null}
          {changeLogEntriesLoading ? (
            <div className="mx-8 my-8 text-sm text-gray-500 dark:text-gray-400">
              Change log wird geladen...
            </div>
          ) : changeLogEntries.length === 0 ? (
            <div className="mx-8 my-8 text-sm text-gray-500 dark:text-gray-400">
              Keine Einträge vorhanden.
            </div>
          ) : (
            <div className="mx-4 my-4 sm:mx-8">
              {changeLogEntries.map((entry, index) => (
                <div key={entry.id} className={`grid ${changeLogGridColumns} gap-x-4`}>
                  <div className="flex items-start gap-2 pb-2 text-sm font-medium text-gray-900 dark:text-gray-100 md:pb-8">
                    {entry.isNew ? (
                      <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full bg-yellow-400" />
                    ) : (
                      <span className="h-2.5 w-2.5 shrink-0" />
                    )}
                    <span>{formatChangelogDate(entry.date)}</span>
                  </div>
                  <div className="relative hidden justify-center pb-8 md:flex">
                    <span className="mt-1 h-2.5 w-2.5 rounded-full bg-black dark:bg-gray-100" />
                    {index < changeLogEntries.length - 1 ? (
                      <span className="absolute top-5 bottom-0 w-px bg-gray-300 dark:bg-gray-600" />
                    ) : null}
                  </div>
                  <div className={`pb-8 text-sm text-gray-700 dark:text-gray-300 ${isSuperAdmin ? 'col-span-2 md:col-span-1' : ''}`}>
                    {renderMarkdown(entry.updateText)}
                  </div>
                  {isSuperAdmin ? (
                    <div className="col-start-2 row-start-1 flex items-start justify-end pb-8 md:col-auto md:row-auto">
                      <IconButton
                        size="small"
                        aria-label="Eintrag Optionen"
                        className="mt-[-6px]"
                        onClick={(event) => handleChangeLogActionMenuOpen(event, entry.id)}
                      >
                        <FiMoreVertical size={16} />
                      </IconButton>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          )}
          <Menu
            anchorEl={changeLogActionMenu.anchorEl}
            open={Boolean(changeLogActionMenu.anchorEl)}
            onClose={handleChangeLogActionMenuClose}
          >
            <MenuItem onClick={handleChangeLogEditClick}>
              Bearbeiten
            </MenuItem>
            <MenuItem
              onClick={handleChangeLogDeleteClick}
              sx={{
                color: '#dc2626',
                '&:hover': {
                  color: '#b91c1c',
                },
              }}
            >
              Löschen
            </MenuItem>
          </Menu>
        </div>
      </Modal>
      <Modal
        open={changeLogDeleteDialogOpen}
        close={handleChangeLogDeleteDialogClose}
        title="Möchten Sie diesen Eintrag wirklich löschen?"
        size="sm"
      >
        <div className="flex flex-col gap-6">
          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              text="Abbrechen"
              onClick={handleChangeLogDeleteDialogClose}
              disabled={changeLogDeleteSubmitting}
            />
            <Button
              variant="primary"
              color="error"
              text="Eintrag löschen"
              loading={changeLogDeleteSubmitting}
              loadingText="Eintrag wird gelöscht..."
              onClick={handleConfirmChangeLogDelete}
            />
          </div>
        </div>
      </Modal>
      <Modal
        open={isChangeLogEntryOpen}
        close={handleChangeLogEntryClose}
        title={editingChangeLogEntryId ? "Eintrag bearbeiten" : "Neuer Eintrag"}
        size="lg"
      >
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Input<ChangeLogEntryFormValues>
              type="select-multiple"
              label="Mandant"
              name="tenantIds"
              formRules={changeLogEntryForm}
              options={tenantOptions}
              value={selectedTenantIds}
              hideSelectedChips
              selectAllLabel="Alle auswählen"
              onChange={(value) => {
                changeLogEntryForm.setValue(
                  'tenantIds',
                  Array.isArray(value) ? value.map(String) : [],
                  { shouldDirty: true, shouldTouch: true }
                );
                changeLogEntryForm.clearErrors('tenantIds');
              }}
              disabled={tenantsLoading}
            />
            <div>
              <div className="text-lg font-medium text-gray-600 dark:text-white">
                Datum
              </div>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale="de"
                localeText={deDE.components.MuiLocalizationProvider.defaultProps.localeText}
              >
                <DatePicker
                  value={changeLogEntryForm.watch('date')}
                  onChange={(value) => {
                    changeLogEntryForm.setValue(
                      'date',
                      value ? dayjs(value) : null,
                      { shouldDirty: true, shouldTouch: true }
                    );
                    changeLogEntryForm.clearErrors('date');
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!changeLogEntryForm.formState.errors.date,
                      helperText: changeLogEntryForm.formState.errors.date?.message,
                    },
                  }}
                />
              </LocalizationProvider>
            </div>
          </div>
          <Input<ChangeLogEntryFormValues>
            type="textarea"
            placeholder="Neuerungen, Verbesserungen oder Fehlerbehebungen (Markdown unterstützt)"
            label="Inhalt des Updates"
            name="updateText"
            formRules={changeLogEntryForm}
            validation={{
              required: {
                value: true,
                message: 'Fülle dieses Feld aus',
              },
            }}
            rows={12}
          />
          <div className="flex justify-end">
            <Button
              variant="primary"
              text={editingChangeLogEntryId ? "Eintrag speichern" : "Neuen Eintrag erstellen"}
              loading={changelogEntrySubmitting}
              loadingText={editingChangeLogEntryId ? "Eintrag wird gespeichert..." : "Eintrag wird erstellt..."}
              onClick={handleCreateChangelogEntry}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Header; 
